/**
 * Shared Action Center seen/resolved store (§7.14 badge composer — Track 4
 * Slice A; DEV-1209 server seen-state adoption).
 *
 * ONE seen set + ONE optimistic-resolved layer, consumed by BOTH the
 * ActionCenterInbox list and the app-wide LeftNav badge so the two can never
 * diverge (§7.14: "badge and list can never diverge"). Previously this state
 * lived inside ActionCenterInbox; the badge counted a different, narrower set.
 *
 * Source of truth:
 * - Server seen-state (DEV-1209, GET/POST /api/action-items/seen) is
 *   AUTHORITATIVE. Hydrated once per user per session and merged in.
 * - localStorage is an OPTIMISTIC CACHE only (instant paint before the server
 *   responds; day-scoped, user-namespaced per DEV-1443).
 * - The resolved layer (Done today + Undo) mirrors the inbox's prior
 *   semantics unchanged: session resolves + day-scoped LS snapshots.
 *
 * Module-level store + useSyncExternalStore so any component (LeftNav, App,
 * ActionCenterInbox) reads the same object without prop threading.
 */

import { useCallback, useEffect, useSyncExternalStore } from "react";
import { fetchSeenItemKeys, markItemsSeen } from "../api/actionItemsSeen";
import type { ActionItem } from "../Pages/ContentApprovals/actionItems";

// ── LS keys (unchanged from ActionCenterInbox; DEV-1443 user-namespaced) ──

const seenLsKey = (userGuid: string) =>
  `l4e_action_center_seen_today:${String(userGuid || "anon").toUpperCase()}`;
const doneLsKey = (userGuid: string) =>
  `l4e_action_center_done_today:${String(userGuid || "anon").toUpperCase()}`;

function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

/** Store only display fields — no workflow/instance/data back-refs.
 *  DEV-1450 §3c: content strings DO persist (description + milestone
 *  nudge/CTA) — the Done tray must never collapse to a bare title.
 *  Workflow content re-attaches at render time via the workflow store
 *  (snapshot id = workflow_guid), so no back-refs are needed here. */
function trimDone(it: ActionItem): ActionItem {
  return {
    id: it.id,
    kind: it.kind,
    typeKey: it.typeKey,
    source: it.source,
    role: it.role,
    subject: it.subject,
    dateLabel: it.dateLabel,
    dateSort: it.dateSort,
    status: "resolved",
    overdue: false,
    base: 0,
    overdueDays: 0,
    dueInDays: null,
    ageDays: 0,
    description: it.description,
    milestone: it.milestone
      ? {
          nudgeText: (it.milestone as any).nudgeText,
          nudgeKey: (it.milestone as any).nudgeKey,
          ctaRoute: (it.milestone as any).ctaRoute,
          ctaState: (it.milestone as any).ctaState,
          ctaLabelKey: (it.milestone as any).ctaLabelKey,
          checklistKey: (it.milestone as any).checklistKey,
          communityGuid: (it.milestone as any).communityGuid,
        }
      : undefined,
  };
}

// ── Store ─────────────────────────────────────────────────────────

export interface ActionItemSeenState {
  userGuid: string | null;
  /** localStorage hydration finished (synchronous, per user). */
  hydrated: boolean;
  /** Server seen-keys merged (authoritative layer arrived). */
  serverHydrated: boolean;
  seenIds: ReadonlySet<string>;
  resolvedIds: ReadonlySet<string>;
  doneSnapshots: ReadonlyMap<string, ActionItem>;
}

let state: ActionItemSeenState = {
  userGuid: null,
  hydrated: false,
  serverHydrated: false,
  seenIds: new Set<string>(),
  resolvedIds: new Set<string>(),
  doneSnapshots: new Map<string, ActionItem>(),
};

const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function setState(patch: Partial<ActionItemSeenState>) {
  state = { ...state, ...patch };
  emit();
}

function persistSeen() {
  if (!state.userGuid) return;
  try {
    window.localStorage.setItem(
      seenLsKey(state.userGuid),
      JSON.stringify({ date: todayKey(), ids: [...state.seenIds] }),
    );
  } catch {
    /* persistence is best-effort */
  }
}

function persistDone() {
  if (!state.userGuid) return;
  try {
    window.localStorage.setItem(
      doneLsKey(state.userGuid),
      JSON.stringify({
        date: todayKey(),
        items: [...state.doneSnapshots.values()].map(trimDone),
      }),
    );
  } catch {
    /* persistence is best-effort */
  }
}

/** Per-user server hydration guard (once per user per session). */
let serverFetchedFor: string | null = null;

/**
 * Switch the store to a user: reset, hydrate the localStorage optimistic
 * cache synchronously, then merge the authoritative server seen-keys.
 * Identity switches reset to the NEW user's state first so nothing carries
 * over (DEV-1443).
 */
export function ensureSeenStoreUser(userGuid: string | null | undefined) {
  const ug = userGuid ? String(userGuid) : null;
  if (state.userGuid === ug && state.hydrated) return;

  // One-time purge of the pre-DEV-1443 shared (un-namespaced) keys.
  try {
    window.localStorage.removeItem("l4e_action_center_seen_today");
    window.localStorage.removeItem("l4e_action_center_done_today");
  } catch {
    /* ignore */
  }

  const seen = new Set<string>();
  const resolved = new Set<string>();
  const snapshots = new Map<string, ActionItem>();

  if (ug) {
    try {
      const raw = window.localStorage.getItem(seenLsKey(ug));
      if (raw) {
        const parsed = JSON.parse(raw) as { date: string; ids: string[] };
        if (parsed?.date === todayKey() && Array.isArray(parsed.ids)) {
          parsed.ids.forEach((id) => seen.add(id));
        }
      }
    } catch {
      /* ignore corrupt/unavailable storage */
    }
    try {
      const raw = window.localStorage.getItem(doneLsKey(ug));
      if (raw) {
        const parsed = JSON.parse(raw) as { date: string; items: ActionItem[] };
        if (parsed?.date === todayKey() && Array.isArray(parsed.items)) {
          parsed.items.forEach((i) => {
            snapshots.set(i.id, i);
            resolved.add(i.id);
          });
        }
      }
    } catch {
      /* ignore corrupt/unavailable storage */
    }
  }

  setState({
    userGuid: ug,
    hydrated: true,
    serverHydrated: serverFetchedFor === ug && ug != null,
    seenIds: seen,
    resolvedIds: resolved,
    doneSnapshots: snapshots,
  });

  // Authoritative layer — server seen-keys, merged over the LS cache.
  if (ug && serverFetchedFor !== ug) {
    serverFetchedFor = ug;
    fetchSeenItemKeys()
      .then((keys) => {
        if (state.userGuid !== ug) return; // identity switched mid-flight
        if (keys?.length) {
          const next = new Set(state.seenIds);
          keys.forEach((k) => next.add(k));
          setState({ seenIds: next, serverHydrated: true });
          persistSeen();
        } else {
          setState({ serverHydrated: true });
        }
      })
      .catch(() => {
        // Offline — LS cache stands; allow a retry on next identity ensure.
        serverFetchedFor = null;
      });
  }
}

/** Optimistic mark-seen: local set + LS cache + fire-and-forget server persist. */
export function markActionItemsSeen(ids: string[]) {
  const missing = ids.filter((id) => !state.seenIds.has(id));
  if (missing.length === 0) return;
  const next = new Set(state.seenIds);
  missing.forEach((id) => next.add(id));
  setState({ seenIds: next });
  persistSeen();
  markItemsSeen(missing).catch(() => {
    /* idempotent; server merge on next session heals */
  });
}

/** Optimistic resolve (Done today) — snapshot preserved for the Done view. */
export function markActionItemResolved(item: ActionItem) {
  const snapshots = new Map(state.doneSnapshots).set(item.id, {
    ...item,
    status: "resolved" as const,
  });
  const resolved = new Set(state.resolvedIds).add(item.id);
  setState({ doneSnapshots: snapshots, resolvedIds: resolved });
  persistDone();
}

/** Undo a resolve. */
export function unresolveActionItem(id: string) {
  const resolved = new Set(state.resolvedIds);
  resolved.delete(id);
  const snapshots = new Map(state.doneSnapshots);
  snapshots.delete(id);
  setState({ resolvedIds: resolved, doneSnapshots: snapshots });
  persistDone();
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot(): ActionItemSeenState {
  return state;
}

/**
 * The ONE unseen predicate (badge AND list — keep singular; §7.14).
 * unseen = not optimistically resolved ∧ not resolved at source ∧ not seen.
 */
export function isUnseenActionItem(
  it: Pick<ActionItem, "id" | "status">,
  s: Pick<ActionItemSeenState, "seenIds" | "resolvedIds">,
): boolean {
  return !s.resolvedIds.has(it.id) && it.status !== "resolved" && !s.seenIds.has(it.id);
}

/** Hook: the shared seen/resolved state for the given user. */
export function useActionItemSeenStore(currentUserGuid: string | null | undefined) {
  const snap = useSyncExternalStore(subscribe, getSnapshot);

  useEffect(() => {
    ensureSeenStoreUser(currentUserGuid);
  }, [currentUserGuid]);

  const markSeen = useCallback((ids: string[]) => markActionItemsSeen(ids), []);
  const markResolved = useCallback((item: ActionItem) => markActionItemResolved(item), []);
  const unresolve = useCallback((id: string) => unresolveActionItem(id), []);

  return {
    seenIds: snap.seenIds,
    resolvedIds: snap.resolvedIds,
    doneSnapshots: snap.doneSnapshots,
    hydrated: snap.hydrated && snap.userGuid === (currentUserGuid || null),
    serverHydrated: snap.serverHydrated,
    markSeen,
    markResolved,
    unresolve,
  };
}
